import type { SelectedOptionValuesRecord, SelectionPayload } from 'src/redux/basket/types';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';

export enum UpdateExisting {
  Dedupe,
  ChangeInPlace,
}

type UtilsFn<T> = (
  selectedOptionValues: SelectedOptionValuesRecord,
  { optionId }: SelectionPayload,
  updateType?: UpdateExisting,
) => T;

const hasExistingOption: UtilsFn<boolean> = (selectedOptionValues, { optionId }) =>
  selectedOptionValues[optionId] != null;

const hasExistingOptionValue: UtilsFn<boolean> = (
  selectedOptionValues,
  { optionId, optionValue },
) => selectedOptionValues[optionId]?.some((opt) => opt.id === optionValue.id) ?? false;

const getUpdatedExistingSelection: UtilsFn<SelectedOptionValuesRecord> = (
  selectedOptionValues,
  { optionId, optionValue },
  updateType,
) => {
  let filteredSelection: CatServiceAPIProductOptionValue[] = [];

  if (updateType === UpdateExisting.Dedupe) {
    filteredSelection = selectedOptionValues[optionId]!.filter((opt) => opt.id !== optionValue.id);
  }

  if (updateType === UpdateExisting.ChangeInPlace) {
    filteredSelection = selectedOptionValues[optionId]!.map((opt) =>
      opt.id === optionValue.id ? optionValue : opt,
    );
  }

  if (filteredSelection.length === 0) {
    const { [optionId]: _, ...rest } = selectedOptionValues;
    return rest;
  }

  return {
    ...selectedOptionValues,
    [optionId]: filteredSelection,
  };
};

export const getUpdatedSelectedOptionValues = (
  selectedOptionValues: SelectedOptionValuesRecord,
  selection: SelectionPayload,
  updateType: UpdateExisting,
): SelectedOptionValuesRecord => {
  const { optionId, optionValue, type } = selection;
  const hasExistingSelection = hasExistingOptionValue(selectedOptionValues, selection);

  if (hasExistingSelection) {
    return getUpdatedExistingSelection(selectedOptionValues, selection, updateType);
  }

  if (type === 'single-selection' || type === 'image-option') {
    return {
      ...selectedOptionValues,
      [optionId]: [optionValue],
    };
  }

  return {
    ...selectedOptionValues,
    [optionId]: hasExistingOption(selectedOptionValues, selection)
      ? selectedOptionValues[optionId]!.concat(optionValue)
      : [optionValue],
  };
};
